import { Box, Button, Typography } from '@mui/material'
import Header from 'components/Header'
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel
} from '@mui/x-data-grid'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingContext } from 'contexts/LoadingContextProvider'
import { deleteBoxesById, getAllBoxes } from 'api/box'
import { getLocationNameById } from 'utils'
import { SnackContext } from 'contexts/SnackContextProvider'
import { SnackbarSeverity } from 'constant'

const BoxList = () => {
  const navigate = useNavigate()
  const [boxes, setBoxes] = useState<any[]>([])
  const { loading, setLoading } = useContext(LoadingContext)
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set()
  })
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10
  })
  const { company } = useParams()
  const { handleOpenSnackbar } = useContext(SnackContext)

  const fetchData = async () => {
    setLoading(true)
    const boxesResult = await getAllBoxes(`${company}`)
    const { boxes } = boxesResult.data
    const mappedBoxes = boxes.map((order: any) => ({
      ...order,
      id: order._id
    }))
    setBoxes(mappedBoxes)
    setLoading(false)
  }

  const deleteBoxes = async (boxIds: string[]) => {
    setLoading(true)
    try {
      const result = await deleteBoxesById(boxIds)
      if (result.data.success) {
        handleOpenSnackbar(
          SnackbarSeverity.Success,
          'Boxes deleted successfully'
        )
        fetchData()
      }
    } catch (error: any) {
      handleOpenSnackbar(
        SnackbarSeverity.Error,
        error.message || 'Failed to delete boxes'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 250 },
    { field: 'boxCode', headerName: 'Box Code', width: 150 },
    { field: 'company', headerName: 'Company', width: 150 },
    {
      field: 'locationId',
      headerName: 'Location',
      width: 150,
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <Typography component='span'>
            {getLocationNameById(params.value)}
          </Typography>
        </Box>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      sortable: false,
      renderCell: params => (
        <Button
          variant='outlined'
          size='small'
          onClick={() => {
            navigate(`${params.row.id}`)
          }}
        >
          View
        </Button>
      )
    }
  ]

  return (
    <Box>
      <Header />
      <Box sx={{ maxWidth: '80%', margin: '20px auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              deleteBoxes(Array.from(selectionModel.ids) as string[])
            }}
            disabled={selectionModel.ids.size === 0}
          >
            Delete
          </Button>
        </Box>
        <DataGrid
          rows={boxes}
          columns={columns}
          loading={loading}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton'
            }
          }}
          checkboxSelection
          rowSelectionModel={selectionModel}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={newPaginationModel =>
            setPaginationModel(newPaginationModel)
          }
          onRowSelectionModelChange={newSelectionModel =>
            setSelectionModel(newSelectionModel)
          }
        />
      </Box>
    </Box>
  )
}

export default BoxList
